import { useState, useCallback, useRef } from 'react';

/**
 * useModals Hook
 * 
 * Manages blocked reason and delete confirmation modal state and handlers.
 */
export const useModals = (tasks, onStatusChange) => {
  const [blockedModalState, setBlockedModalState] = useState({ 
    isOpen: false, 
    taskId: null, 
    taskName: '',
    kanbanOrder: null, // Track the drop position for when modal confirms
  });

  // Delete confirmation modal state (for tasks with nested campaigns)
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    taskId: null,
    taskName: '',
    nestedSprintName: '',
    deleteOptions: null, // forwarded to the delete handler on confirm (e.g. { bridge: true })
  });

  // Delete ALL missions confirmation (Ctrl+A + Delete in Flow)
  const deleteAllConfirmRef = useRef(null);
  const [deleteAllModalState, setDeleteAllModalState] = useState({
    isOpen: false,
    taskCount: 0,
    campaignName: '',
  });

  // Reset campaign progress confirmation
  const resetCampaignConfirmRef = useRef(null);
  const [resetCampaignModalState, setResetCampaignModalState] = useState({
    isOpen: false,
    loading: false,
    isMulti: false,
    rootMissionCount: 0,
    nestedMissionCount: 0,
    variableValueCount: 0,
    checkboxCount: 0,
    documentCount: 0,
    fileCount: 0,
  });

  // Handle status change with blocked modal
  // kanbanOrder is the position index where the card should be placed in the column
  const handleStatusChangeWithModal = useCallback((taskId, newStatus, blockedReason = null, kanbanOrder = null) => {
    if (newStatus === 'blocked' && blockedReason === null) {
      const task = tasks.find(t => t.id === taskId);
      setBlockedModalState({
        isOpen: true,
        taskId,
        taskName: task?.name || 'Unknown Task',
        kanbanOrder, // Store for use when modal confirms
      });
      return;
    }
    
    onStatusChange(taskId, newStatus, blockedReason, kanbanOrder);
  }, [tasks, onStatusChange]);

  // Handle blocked modal confirm
  const handleBlockedConfirm = useCallback((reason) => {
    if (blockedModalState.taskId) {
      onStatusChange(blockedModalState.taskId, 'blocked', reason, blockedModalState.kanbanOrder);
    }
    setBlockedModalState({ isOpen: false, taskId: null, taskName: '', kanbanOrder: null });
  }, [blockedModalState.taskId, blockedModalState.kanbanOrder, onStatusChange]);

  // Handle blocked modal close
  const handleBlockedModalClose = useCallback(() => {
    setBlockedModalState({ isOpen: false, taskId: null, taskName: '', kanbanOrder: null });
  }, []);

  // Handle opening delete confirmation modal (for tasks with nested campaigns)
  const handleOpenDeleteModal = useCallback((taskId, nestedSprintName, deleteOptions = null) => {
    const task = tasks.find(t => t.id === taskId);
    if (task) {
      setDeleteModalState({
        isOpen: true,
        taskId,
        taskName: task.name || 'Unknown Task',
        nestedSprintName: nestedSprintName || task.name || 'Untitled Campaign',
        deleteOptions,
      });
    }
  }, [tasks]);

  // Handle delete modal close
  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalState({ isOpen: false, taskId: null, taskName: '', nestedSprintName: '', deleteOptions: null });
  }, []);

  // Handle delete modal confirm - returns { taskId, deleteOptions } so caller can perform the delete
  const handleDeleteModalConfirm = useCallback(() => {
    const { taskId, deleteOptions } = deleteModalState;
    setDeleteModalState({ isOpen: false, taskId: null, taskName: '', nestedSprintName: '', deleteOptions: null });
    return { taskId, deleteOptions };
  }, [deleteModalState]);

  const handleOpenDeleteAllModal = useCallback(({ taskCount, campaignName, onConfirm }) => {
    deleteAllConfirmRef.current = onConfirm;
    setDeleteAllModalState({
      isOpen: true,
      taskCount: taskCount || 0,
      campaignName: campaignName || 'this campaign',
    });
  }, []);

  const handleDeleteAllModalClose = useCallback(() => {
    deleteAllConfirmRef.current = null;
    setDeleteAllModalState({ isOpen: false, taskCount: 0, campaignName: '' });
  }, []);

  const handleDeleteAllModalConfirm = useCallback(() => {
    const onConfirm = deleteAllConfirmRef.current;
    deleteAllConfirmRef.current = null;
    setDeleteAllModalState({ isOpen: false, taskCount: 0, campaignName: '' });
    onConfirm?.();
  }, []);

  const handleOpenResetCampaignModal = useCallback(({
    isMulti = false,
    rootMissionCount = 0,
    nestedMissionCount = 0,
    variableValueCount = 0,
    onConfirm,
  } = {}) => {
    resetCampaignConfirmRef.current = onConfirm;
    setResetCampaignModalState({
      isOpen: true,
      loading: true,
      isMulti: !!isMulti,
      rootMissionCount: rootMissionCount || 0,
      nestedMissionCount: nestedMissionCount || 0,
      variableValueCount: variableValueCount || 0,
      checkboxCount: 0,
      documentCount: 0,
      fileCount: 0,
    });
  }, []);

  const handleUpdateResetCampaignModal = useCallback((patch) => {
    setResetCampaignModalState((prev) => (prev.isOpen ? { ...prev, ...patch } : prev));
  }, []);

  const handleResetCampaignModalClose = useCallback(() => {
    resetCampaignConfirmRef.current = null;
    setResetCampaignModalState({
      isOpen: false,
      loading: false,
      isMulti: false,
      rootMissionCount: 0,
      nestedMissionCount: 0,
      variableValueCount: 0,
      checkboxCount: 0,
      documentCount: 0,
      fileCount: 0,
    });
  }, []);

  const handleResetCampaignModalConfirm = useCallback(() => {
    const onConfirm = resetCampaignConfirmRef.current;
    resetCampaignConfirmRef.current = null;
    setResetCampaignModalState({
      isOpen: false,
      loading: false,
      isMulti: false,
      rootMissionCount: 0,
      nestedMissionCount: 0,
      variableValueCount: 0,
      checkboxCount: 0,
      documentCount: 0,
      fileCount: 0,
    });
    onConfirm?.();
  }, []);

  return {
    blockedModalState,
    deleteModalState,
    deleteAllModalState,
    resetCampaignModalState,
    handleStatusChangeWithModal,
    handleBlockedConfirm,
    handleBlockedModalClose,
    handleOpenDeleteModal,
    handleDeleteModalClose,
    handleDeleteModalConfirm,
    handleOpenDeleteAllModal,
    handleDeleteAllModalClose,
    handleDeleteAllModalConfirm,
    handleOpenResetCampaignModal,
    handleUpdateResetCampaignModal,
    handleResetCampaignModalClose,
    handleResetCampaignModalConfirm,
  };
};

export default useModals;
